import { QuestionPayload, QuestionPayloadWithContext, QuestionResponse } from "../../../../src/types/chat";
import { apiFetch } from "../api";
import { MessageSuggestionContext } from "../commitments/types";
import { consumeChatSSE, ChatStreamCallbacks } from "./sse";

export type { MessageSuggestionContext, ChatStreamCallbacks };

function asQuestionResponse(data: Record<string, unknown>): QuestionResponse {
    return {
        response_json: String(data.response_json ?? ""),
        natural_language_response: String(data.natural_language_response ?? ""),
        cypher_query: String(data.cypher_query ?? ""),
    };
}

export const getResponseFromQuestion = async (
    payload: QuestionPayload,
    callbacks: ChatStreamCallbacks = {}
): Promise<QuestionResponse> => {
    const data = await consumeChatSSE(
        `/chat/run_qa`,
        {
            method: "POST",
            body: JSON.stringify({
                question: payload.question,
                attachments: payload.attachments,
            }),
        },
        {
            onStatus: callbacks.onStatus,
            onToken: callbacks.onToken,
        }
    );
    return asQuestionResponse(data);
};

export const getResponseFromQuestionWithContext = async (
    payload: QuestionPayloadWithContext,
    callbacks: ChatStreamCallbacks = {}
): Promise<QuestionResponse> => {
    const data = await consumeChatSSE(
        `/chat/run_contextualized_qa`,
        {
            method: "POST",
            body: JSON.stringify({
                question: payload.question,
                context: payload.context,
                attachments: payload.attachments,
            }),
        },
        {
            onStatus: callbacks.onStatus,
            onToken: callbacks.onToken,
        }
    );
    return asQuestionResponse(data);
};

export const runMessageSuggestion = async (
    context: MessageSuggestionContext,
    callbacks: ChatStreamCallbacks = {}
): Promise<QuestionResponse> => {
    const data = await consumeChatSSE(
        `/chat/run_message_suggestion`,
        {
            method: "POST",
            body: JSON.stringify({ context }),
        },
        {
            onStatus: callbacks.onStatus,
            onToken: callbacks.onToken,
        }
    );
    return asQuestionResponse(data);
};

export interface MessageFeedbackPayload {
    answer: string;
    question: string;
    cypher_query: string;
    liked: string;
}

export const setMessageFeedback = async (payload: MessageFeedbackPayload): Promise<any> => {
    const data = await apiFetch(`/chat/set_message_feedback`, {
        method: "POST",
        body: JSON.stringify(payload),
    });
    return data;
};

export interface HistoryAttachment {
    filename: string;
    mime_type: string;
    s3_key: string;
}

export interface HistoryMessage {
    question: string;
    answer: string;
    context: string | object;
    attachments?: HistoryAttachment[];
}

export interface HistoryResponse {
    history: HistoryMessage[];
}

export const getUserHistoryMessages = async (): Promise<HistoryResponse> => {
    const data = await apiFetch(`/chat/get_user_history_msgs`);
    return data as HistoryResponse;
};
